/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2022 Metrological
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Lightning, Utils, Router, Log, AppData } from '@lightningjs/sdk'
import { AppSetup } from './lib/app-setup'
import { Config } from './lib/config'
import { Platform } from './platforms/platform'
import { routes } from './routes'
import MenuWidget from './widgets/menu'
import OptionsMenuWidget from './widgets/options'
import AboutWidget from './widgets/about'
import LoadingWidget from './widgets/loading'
import { Logger } from './lib/logger'
import NotificationWidget from './widgets/notification'
import BookmarkWidget from './widgets/bookmark'
import { KeyCodes } from './lib/key-codes'
import DialogWidget from './widgets/dialog'
import { Lifecycle } from '@firebolt-js/sdk'
import VideoResume from './ui-elements/video-resume'
import MoreinfoWidget from './widgets/moreinfo'
import ParentalControlWidget from './widgets/parental'
import { GestureRecognizer } from './lib/GestureRecognizer'

export interface AppTemplateSpec extends Router.App.TemplateSpec {
    Pages: {},
    Widgets: {
        Menu: object,
        OptionsMenu: {},
        About: {},
        LoadingScreen: {},
        Notification: {}
    },
}



export class App extends Router.App<AppTemplateSpec> implements Lightning.Component.ImplementTemplateSpec<AppTemplateSpec> {
    //public appState: AppState | null = null
    static appConfig: Config
    static platform: Platform
    gesture!: GestureRecognizer
    dragComponent!: Lightning.Element | null

    static getFonts() {
        return []
    }

    static override _template(): Lightning.Component.Template<AppTemplateSpec> {
        return {
            w: 1920,
            h: 1080,
            rect: true,
            color: 0x00000000,
            Pages: {
                forceZIndexContext: true,
            },
            Loading: {},
            Widgets: {
                zIndex: 50,
                Menu: {
                    zIndex: 80,
                    type: MenuWidget,
                },
                OptionsMenu: {
                    zIndex: 100,
                    type: OptionsMenuWidget
                },
                // About: {
                //     zIndex: 50,
                //     type: AboutWidget
                // },
                LoadingScreen: {
                    zIndex: 600,
                    type: LoadingWidget
                },
                Dialog: {
                    zIndex: 700,
                    type: DialogWidget
                },
                Notification: {
                    zIndex: 800,
                    type: NotificationWidget
                },
                Bookmark: {
                    zIndex: 90,
                    type: BookmarkWidget,
                    visible: false
                },
                Moreinfo: {
                    zIndex: 60,
                    type: MoreinfoWidget,
                    visible: false
                },
                ParentalControl: {
                    zIndex: 80,
                    forceZIndexContext: true,
                    type: ParentalControlWidget
                }
            }
        }
    }

    _handleClick(target: any): boolean | void {
        Log.info('APP _handleClick:', target)
    }

    
    $toggleMenuVisibility(visible: boolean) {
        this.tag('Widgets.Menu').alpha = visible ? 1 : 0
    }

    // override _handleKey(e: KeyboardEvent): boolean | void {
    //   Log.info('APP _handleKey:', e.code)
    //   return true
    // }

    // override _captureKey(e: KeyboardEvent): boolean | void {
    //     if (e && (e.keyCode == KeyCodes.KEY_EXIT || e.keyCode == KeyCodes.KEY_HOME)) {
    //         const { appState } = AppData as AppSetup;
    //         //appState!.appUnload = true;
    //         if (appState!.gridMode) {
    //             Logger.processUserAction('userExitedViaRemoteDirectlyFromGrid');
    //         }
    //     }
    //     return false
    // }

    override _setup() {
        const { appConfig } = AppData as AppSetup
        if (appConfig.enableGestureListener) {
            this.gesture = new GestureRecognizer(document.body);

            this.gesture.on('tap', (detail) => {
                Log.info('Touch event', detail)
                this.dragComponent = null;
                app.application.fireBottomUpClickHandler(detail.originalEvent)
            })

            this.gesture.on('swipe', (detail) => {
                this.dragComponent = null;
                Log.info('Swipe event', detail, this)
                let e: KeyboardEvent
                if (detail.direction == 'left') {
                    e = new KeyboardEvent('keydown', { key: 'ArrowRight', keyCode: 39, isComposing: false, repeat: true });
                } else if (detail.direction == 'right') {
                    e = new KeyboardEvent('keydown', { key: 'ArrowLeft', keyCode: 37, isComposing: false, repeat: true });
                } else if (detail.direction == 'down') {
                    e = new KeyboardEvent('keydown', { key: 'ArrowUp', keyCode: 38, isComposing: false, repeat: true });
                } else if (detail.direction == 'up') {
                    e = new KeyboardEvent('keydown', { key: 'ArrowDown', keyCode: 40, isComposing: false, repeat: true });
                }
                app.application._receiveKeydown(e)
            });

            this.gesture.on('longTouch', (detail) => {
                this.dragComponent = null;
                const longEvent = new KeyboardEvent('keydown', { key: 'LongEnter', keyCode: KeyCodes.KEY_LONG_OK, isComposing: false, repeat: false });
                detail.originalEvent.stopPropagation();
                detail.originalEvent.preventDefault();
                app.application._receiveKeydown(longEvent);
            });

            this.gesture.on('touchstart', (detail) => {
                this.dragComponent = app.application._getTargetChild(detail.startPosition.x, detail.startPosition.y)
                if (this.dragComponent !== null) {
                    if (Router.getActivePage()._handleTouchStart) {
                        Router.getActivePage()._handleTouchStart(detail, this.dragComponent)
                    }
                }
            });

            this.gesture.on('click', (detail) => {
                app.application.fireBottomUpClickHandler(detail.originalEvent)
            });

            this.gesture.on('hover', (detail) => {
                app.application.fireBottomUpHoverHandler({ clientX: detail.position.x, clientY: detail.position.y })
            })

            this.gesture.on('drag', (detail) => {
                if (this.dragComponent !== null) {
                    if (Router.getActivePage()._handleDrag) {
                        Router.getActivePage()._handleDrag(detail, this.dragComponent)
                    }
                }
            })

            this.gesture.on('wheel', (detail) => {
                Log.info('Wheel event', detail)
                app.application._recieveScrollWheel(detail.originalEvent)
            })
        }
        Router.startRouter(routes, this)
    }

    override _init(): void {
        try {
            let lifecycleListinerId: Promise<number> = Lifecycle.listen('unloading', value => {
                Log.info('APP LifeCycle UNLOADING Event')
                const { appState } = AppData as AppSetup;
                if (appState!.videoMode) {
                    const app = appState?.getApp()!;
                    if (app.videoObj) {
                        app.videoObj.hitCloseTracker();
                    }
                }

                try {
                    Lifecycle.finished()
                } catch (error) {
                    Log.error("Lifecycle.finished ", error)
                }
            })
        } catch (error) {
            Log.error("Lifecycle.listen unloading ", error)
        }

        window.addEventListener("unload", function logData() {
            /*const { appState }= AppData as AppSetup;
            appState!.appUnload = true;
            if (appState!.videoMode) {
              const app = appState?.getApp()!;
              if (app.videoObj) {
                app.videoObj.hitCloseTracker();
              }
              Logger.processUserAction('userExitedViaRemoteDirectly');
            } else if(appState!.gridMode){
              Logger.processUserAction('userExitedViaRemoteDirectlyFromGrid');
            }*/
        });
    }
}
